import { useApolloClient, useMutation, gql } from '@apollo/client';
import React from 'react';
import EventsSynchronizer from '~Components/EventsSynchronizer';
import {
  AddInventoryEvents,
  AddInventoryEventsVariables,
  GetInventoryEvents,
  GetInventoryEventsVariables,
} from './__queries__';

const INVENTORY_EVENTS_SNAPSHOTS_QUERY = gql`
  query GetInventorySnapshot($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      inventory {
        snapshot
      }
    }
  }
`;

const INVENTORY_EVENTS_QUERY = gql`
  query GetInventoryEvents($venueId: String!, $sentAfter: DateTime!) {
    inventoryEvents(venueId: $venueId, sentAfter: $sentAfter) {
      lastKey
      items
    }
  }
`;

const ADD_INVENTORY_EVENT_MUTATION = gql`
  mutation AddInventoryEvents($venueId: String!, $events: Json!) {
    addInventoryEvents(venueId: $venueId, events: $events) {
      success
      message
    }
  }
`;

interface Props {
  venueId: string;
}

const InventorySynchronizer: React.FunctionComponent<Props> = ({ venueId }) => {
  const client = useApolloClient();

  /**
   * Set up the add table events mutation
   */

  const [addInventoryEvents] = useMutation<AddInventoryEvents, AddInventoryEventsVariables>(
    ADD_INVENTORY_EVENT_MUTATION,
  );

  return (
    <EventsSynchronizer
      venueId={venueId}
      eventType="ADD_INVENTORY_EVENT"
      snapshotType="SET_INVENTORY_SNAPSHOT"
      eventSelector={(state) => state.inventoryEvents}
      snapshotSelector={(state) => state.inventorySnapshot}
      pollingInterval={8000}
      /* Polling query for new events */
      queryEvents={async (timestamp) => {
        const response = await client.query<GetInventoryEvents, GetInventoryEventsVariables>({
          query: INVENTORY_EVENTS_QUERY,
          variables: {
            venueId,
            sentAfter: timestamp,
          },
          fetchPolicy: 'network-only',
        });

        return {
          success: Boolean(response.data?.inventoryEvents?.items),
          events: response.data?.inventoryEvents?.items || [],
          error: response.error?.message || null,
        };
      }}
      /* Snapshot query */
      querySnapshot={async () => {
        const response = await client.query({
          query: INVENTORY_EVENTS_SNAPSHOTS_QUERY,
          variables: {
            venueId,
          },
          fetchPolicy: 'network-only',
        });

        const snapshot = response.data?.venue?.inventory?.snapshot;

        return {
          success: Boolean(snapshot),
          snapshot: snapshot || null,
          timestamp: snapshot?.date || null,
          error: response.error?.message || null,
        };
      }}
      addEventMutation={async (events) => {
        const response = await addInventoryEvents({
          variables: {
            venueId,
            events,
          },
        });

        if (!response.data?.addInventoryEvents?.success) {
          return {
            success: false,
            error: response.errors?.map((error) => error.message).join(', ') || 'Unknown',
          };
        }

        return {
          error: null,
          success: true,
        };
      }}
    />
  );
};

export default InventorySynchronizer;
